import { Box, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { HotkeyCompliantDataGrid } from "src/components/common/dataGrid";
import { useTenantTranslation } from "hooks/formatters";
import { MixMaterialSummary } from "src/store/api/generatedApi";
import { Period } from "hooks/periodIndex";

const INFINITY_DISPLAY_VALUE = "∞";

interface InventoryDepletionTabProps {
  mixMaterialSummary: MixMaterialSummary[];
  chefGroupName: string;
  period: Period;
  mixNumber: number;
}

interface MaterialDepletionRow {
  id: string;
  material: string;
  heatsUntilDepletion: number | null;
}

const sortByDepletion = (
  a: MaterialDepletionRow,
  b: MaterialDepletionRow
): number => {
  if (a.heatsUntilDepletion === null && b.heatsUntilDepletion === null)
    return a.material.localeCompare(b.material);
  if (a.heatsUntilDepletion === null) return 1;
  if (b.heatsUntilDepletion === null) return -1;
  if (a.heatsUntilDepletion === b.heatsUntilDepletion)
    return a.material.localeCompare(b.material);
  return a.heatsUntilDepletion - b.heatsUntilDepletion;
};

export const InventoryDepletionTab = ({
  mixMaterialSummary,
  chefGroupName,
  period,
  mixNumber,
}: InventoryDepletionTabProps) => {
  const { t } = useTenantTranslation();

  const rows: MaterialDepletionRow[] = mixMaterialSummary
    .filter(
      (summary) =>
        summary.chef_group_name === chefGroupName &&
        summary.period === period &&
        summary.mix_number === mixNumber
    )
    .map((summary) => ({
      id: summary.material,
      material: summary.material,
      heatsUntilDepletion: summary.heats_until_depletion ?? null,
    }))
    .sort(sortByDepletion);

  const columns: GridColDef<MaterialDepletionRow>[] = [
    {
      field: "material",
      headerName: t("material"),
      flex: 1,
      sortable: false,
    },
    {
      field: "heatsUntilDepletion",
      headerName: t("heatsUntilDepletion"),
      flex: 1,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ value }) => (
        <Typography variant="body2Mono">
          {value ?? INFINITY_DISPLAY_VALUE}
        </Typography>
      ),
    },
  ];

  return (
    <Stack sx={{ py: 2 }}>
      <Box sx={{ maxWidth: 400 }}>
        <HotkeyCompliantDataGrid autoHeight rows={rows} columns={columns} />
      </Box>
    </Stack>
  );
};
